import {
  cacheKeys,
  FileFilterModel,
  useCurrentWorkspace,
  useFileFilter,
  useSetFileFilter,
  useWorkspaceCategories
} from '@gripp/shared-logic';
import { yupResolver } from '@hookform/resolvers/yup';
import { useCallback, useEffect, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useQueryClient } from '@tanstack/react-query';
import {
  Platform,
  View
} from 'react-native';
import { array, string, object as yupObject } from 'yup';
import { FormContext } from '../forms';
import { DropdownFooter } from '../modals/dropdownFooter';
import { ApplyButton } from './applyButton';
import { CategorySearchInput } from './categorySearchInput';
import { EditableItemFilter } from './editableItemFilter';
import { FilterType } from './filterButton';
import { FilterHeader } from './filterHeader';
import { filterStyles as styles } from './filterStyle';
import { useEditableItemFilter } from './useEditableItemFilter';

const UPDATE_CATEGORY_MUTATION = `
  mutation UpdateCategory($input: MutateCategoryInput!) {
    updateCategory(input: $input) {
      id
      name
    }
  }
`;

const DELETE_CATEGORY_MUTATION = `
  mutation DeleteCategory($input: MutateCategoryInput!) {
    deleteCategory(input: $input) {
      id
    }
  }
`;

const FILES_BY_CATEGORY_QUERY = `
  query FilesByCategory($filter: ListFilesFilter) {
    media {
      listFiles(filter: $filter) {
        count
      }
    }
  }
`;

interface FileCategoriesFilterScreenProps {
  goBack: () => void;
  type: FilterType;
}

export const FileCategoriesFilter = ({
  goBack,
  type,
}: FileCategoriesFilterScreenProps) => {
  const { t } = useTranslation();
  const workspace = useCurrentWorkspace();
  const { data: filter } = useFileFilter();
  const { mutateAsync: setFileFilter } = useSetFileFilter();

  const isWeb = Platform.OS === 'web';
  const workspaceId = typeof workspace === 'string' ? workspace : workspace?.id;

  const formSchema = yupObject({
    orderBy: string().optional().nullable(),
    categoryIds: array().of(string()).optional().nullable(),
  });

  const formMethods = useForm({
    resolver: yupResolver(formSchema),
    mode: 'onChange',
    values: {
      orderBy: filter?.orderBy,
      categoryIds: filter?.categoryIds,
    },
  });
  const {
    control,
    handleSubmit,
    reset,
    formState: { isDirty },
    watch,
  } = formMethods;

  const formData = watch();

  const isCategoriesFilter = type === FilterType.FILECATEGORIES;

  const resetForm = useCallback(() => {
    reset({
      ...formData,
      categoryIds: [],
    });
  }, [reset, formData]);

  const onReset = async () => {
    const updatedFields = {
      categoryIds: [],
    };

    resetForm();

    const mergedFilter = { ...filter, ...updatedFields };
    await setFileFilter(mergedFilter);
    goBack();
  };

  const onSubmit = async (data: any) => {
    await setFileFilter(
      new FileFilterModel({ ...filter, ...data })
    );
    goBack();
  };

  const onPressCategoryFilter = (
    categoryId: string,
    value: (string | undefined)[],
    onChange: (...event: any[]) => void
  ) => {
    let newValue = [];
    if (!value) {
      newValue = [categoryId];
    } else if (!value.includes(categoryId)) {
      newValue = value.concat(categoryId);
    } else {
      newValue = value.filter((id: string | undefined) => id !== categoryId);
    }
    onChange(newValue);
  };

  const canReset = useCallback(() => {
    const orderBy = formData.orderBy;
    const categoryIds = formData.categoryIds;
    const defaultFilter = new FileFilterModel();

    const undefinedIfEmpty = (value?: (string | undefined)[] | null) => {
      return value?.length === 0 ? undefined : value;
    };

    return (
      orderBy !== defaultFilter.orderBy ||
      undefinedIfEmpty(categoryIds) !== defaultFilter.categoryIds
    );
  }, [formData]);

  useEffect(() => {
    if (!filter) {
      resetForm();
    }
  }, [filter, resetForm]);

  // Initialize hook with empty editingCategoryId first
  // Search state
  const [search, setSearch] = useState('');
  const [filteredCategories, setFilteredCategories] = useState<Array<{ id: string; name: string }>>([]);
  const queryClient = useQueryClient();

  const {
    items: allCategories,
    editingItemId: editingCategoryId,
    editingItemName: editingCategoryName,
    openEditMenu,
    setEditingItemName: setEditingCategoryName,
    handleDelete,
    onConfirmDelete,
    confirmDeleteVisible,
    onConfirmModalClose,
    closeEditMenu,
    anchorPos,
    itemRefs,
    itemCount,
  } = useEditableItemFilter({
    itemType: 'category',
    workspaceId,
    useWorkspaceItemsHook: useWorkspaceCategories,
    updateMutation: { modelName: 'Category', query: UPDATE_CATEGORY_MUTATION },
    deleteMutation: { 
      query: DELETE_CATEGORY_MUTATION,
      options: {
        onSuccess: async () => {
          await queryClient.invalidateQueries({ queryKey: cacheKeys('media').all });
        },
      },
    },
    countQuery: {
      cacheKey: ['filesByCategory'],
      query: FILES_BY_CATEGORY_QUERY,
      variables: {
        filter: {
          ...(filter?.categoryIds && { categoryIds: filter.categoryIds }),
        },
      },
      options: {},
    },
    refetchItems: () => {}, // Placeholder, actual refetch is handled by useWorkspaceCategories
    setFilter: async (newFilter: any) => {
      await setFileFilter(new FileFilterModel({ ...filter, ...newFilter }));
    },
    currentFilter: filter,
  });

  // Filter categories based on search
  useEffect(() => {
    const filtered = allCategories.filter(
      (category: any) =>
        category &&
        category.name &&
        category.name.toLowerCase().includes(search.toLowerCase())
    );
    setFilteredCategories(filtered);
  }, [allCategories, search]);

  return (
    <>
      <FormContext.Provider value={formMethods}>
        {!isWeb && (
          <FilterHeader canReset={canReset} onReset={onReset} goBack={goBack} />
        )}
        <View style={styles.container}>
          <View
            style={[
              styles.scrollViewContainer,
              isWeb ? { height: 416 } : { marginBottom: 30 },
            ]}
          >
            <Controller
              control={control}
              name={'categoryIds'}
              render={({ field: { onChange, value } }) => (
                <EditableItemFilter
                  items={filteredCategories}
                  selectedIds={value || []}
                  onItemToggle={onPressCategoryFilter}
                  onChange={onChange}
                  editingItemId={editingCategoryId}
                  editingItemName={editingCategoryName}
                  onEditItem={openEditMenu}
                  onEditNameChange={setEditingCategoryName}
                  onDeleteItem={handleDelete}
                  onConfirmDelete={onConfirmDelete}
                  confirmDeleteVisible={confirmDeleteVisible}
                  onConfirmModalClose={onConfirmModalClose}
                  onCloseEditMenu={closeEditMenu}
                  anchorPos={anchorPos}
                  itemRefs={itemRefs}
                  searchComponent={<CategorySearchInput value={search} onChangeText={setSearch} />}
                  editTitle={t('categories.editCategories')}
                  deleteButtonText={t('categories.delete')}
                  confirmTextLine1={t('categories.confirmTextLine1', { categoryName: editingCategoryName || '' })}
                  confirmTextLine2={t('categories.confirmTextLine2', { fileCount: itemCount || 0 })}
                  yesText={t('categories.yes')}
                  cancelText={t('categories.cancel')}
                  itemCount={itemCount}
                />
              )}
            />
          </View>

          {isWeb ? (
            <DropdownFooter
              isDirty={isDirty || canReset()}
              onSubmit={handleSubmit(onSubmit)}
              canReset={canReset}
              onReset={onReset}
            />
          ) : (
            <ApplyButton isDirty={isDirty || canReset()} onSubmit={handleSubmit(onSubmit)} />
          )}
        </View>
      </FormContext.Provider>
    </>
  );
};